import { PlayerNote } from '~/interfaces';
import { classifyNote, NoteQuality } from '~/modules/game-engine/game-state/helpers/calculate-note-stats';

/**
 * Note qualities worth remarking on. An `ok` note is fine, and saying so every time is noise.
 *
 * Named as a type as well as a list so whatever renders these has to have something to show for
 * every one of them - the gate used to be the lookup table itself, and splitting the two apart
 * without tying them back together would let a quality be announced with no way to draw it.
 */
export type AnnouncedQuality = Extract<NoteQuality, 'perfect' | 'good' | 'missed'>;

export const ANNOUNCED: AnnouncedQuality[] = ['perfect', 'good', 'missed'];

const isAnnounced = (quality: NoteQuality): quality is AnnouncedQuality =>
  ANNOUNCED.includes(quality as AnnouncedQuality);

/** One player cannot produce useful feedback faster than this, and reading it would be a blur. */
export const THROTTLE_MS = 400;

export interface FeedbackDecision {
  quality: AnnouncedQuality;
}

interface Args {
  /** Everything the player has sung so far. */
  notes: PlayerNote[];
  /** How many there were at the previous look, or `undefined` if this is the first. */
  seen: number | undefined;
  /** When this player was last told something, as a timestamp. */
  lastShownAt: number | undefined;
  now: number;
  /** The tolerance the singers chose - the quality bands are relative to it. */
  tolerance: number;
}

/**
 * Whether to say anything about the note this player just finished, and what.
 *
 * Kept out of the component because two of these rules only show themselves over time - that
 * nothing is said twice inside the throttle, and that whatever was already sung when the overlay
 * appeared is not announced - and neither is reachable from a test while they live inside a
 * polling loop that reads a live game.
 */
export const pickFeedback = ({ notes, seen, lastShownAt, now, tolerance }: Args): FeedbackDecision | null => {
  // Whatever had already been sung when this mounted is not news - without this, restarting a song
  // replays the whole run at once.
  if (seen === undefined) return null;

  // The last note is still being sung, so the one before it is the one that just finished.
  if (notes.length <= seen || notes.length < 2) return null;

  const quality = classifyNote(notes[notes.length - 2], tolerance);
  if (!isAnnounced(quality)) return null;

  if (now - (lastShownAt ?? 0) < THROTTLE_MS) return null;

  return { quality };
};
